import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, dateStr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Receipt, AlertTriangle, IndianRupee, Plus, TrendingUp, Wallet, Download, Send, ListTree } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

function InvoicePdfButton({ invoiceId, invoiceNo }) {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/invoices/${invoiceId}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${(invoiceNo || "").replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
    finally { setLoading(false); }
  };
  return (
    <Button size="sm" variant="outline" onClick={download} disabled={loading}
      data-testid={`invoice-pdf-${invoiceId}`} className="h-7 text-xs gap-1">
      <Download className="h-3 w-3" /> PDF
    </Button>
  );
}

function MiniStat({ label, value, tone = "muted", ...rest }) {
  const toneCls = tone === "good" ? "text-emerald-700" : tone === "warn" ? "text-amber-800" : tone === "bad" ? "text-red-700" : "";
  return (
    <div className="rounded-md border p-2 bg-muted/30" {...rest}>
      <div className="text-[10px] uppercase font-mono text-muted-foreground">{label}</div>
      <div className={`font-semibold text-sm ${toneCls}`}>{inr(value || 0, { compact: true })}</div>
    </div>
  );
}


export default function MIS() {
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [aging, setAging] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showPayment, setShowPayment] = useState(null); // invoice id
  const [wbsProject, setWbsProject] = useState("");
  const [wbsData, setWbsData] = useState(null);

  const load = async () => {
    const [p, i, a, s] = await Promise.all([
      api.get("/projects"), api.get("/invoices"),
      api.get("/mis/aging"), api.get("/mis/summary"),
    ]);
    setProjects(p.data); setInvoices(i.data); setAging(a.data); setSummary(s.data);
    if (p.data.length && !wbsProject) setWbsProject(p.data[0].project_id);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!wbsProject) { setWbsData(null); return; }
    (async () => {
      try {
        const r = await api.get(`/mis/wbs/${wbsProject}`);
        setWbsData(r.data);
      } catch { setWbsData(null); }
    })();
  }, [wbsProject, invoices]);

  const agingData = aging ? [
    { bucket: "Current", value: aging.current },
    { bucket: "0-30", value: aging["0-30"] },
    { bucket: "31-60", value: aging["31-60"] },
    { bucket: "61-90", value: aging["61-90"] },
    { bucket: "90+", value: aging["90+"] },
  ] : [];

  const byClient = {};
  invoices.forEach((i) => {
    if (!byClient[i.client]) byClient[i.client] = { client: i.client, billed: 0, collected: 0, outstanding: 0 };
    byClient[i.client].billed += Number(i.amount || 0);
    byClient[i.client].collected += Number(i.paid_amount || 0);
    byClient[i.client].outstanding += Number(i.outstanding || 0);
  });
  const clientRows = Object.values(byClient);

  return (
    <div className="space-y-6" data-testid="mis-root">
      <SectionHeader
        title="MIS & Billing follow-up"
        subtitle="Invoices, collections, aging and management reports"
        right={
          <div className="flex gap-2">
            <SendRemindersButton onDone={load} />
            <NewInvoiceDialog projects={projects} onCreated={load} />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="mis-billed" label="Billed Y-T-D" value={summary?.total_billed ?? 0} compact icon={Receipt} />
        <KPI testId="mis-collected" label="Collected" value={summary?.total_collected ?? 0} compact icon={IndianRupee} tone="good" />
        <KPI testId="mis-outstanding" label="Outstanding" value={summary?.total_outstanding ?? 0} compact icon={Wallet} tone="warn" />
        <KPI testId="mis-overdue" label="Overdue" value={summary?.overdue_amount ?? 0} compact icon={AlertTriangle} tone="bad" />
      </div>

      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="wbs" data-testid="tab-mis-wbs">By Milestone / WBS</TabsTrigger>
          <TabsTrigger value="aging" data-testid="tab-aging">Aging report</TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-client">Client-wise MIS</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-display">All invoices & RA bills</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Invoice</th>
                    <th className="py-2 font-medium">Client</th>
                    <th className="py-2 font-medium">Type</th>
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium">Due</th>
                    <th className="py-2 font-medium text-right">Amount</th>
                    <th className="py-2 font-medium text-right">Net Recv.</th>
                    <th className="py-2 font-medium text-right">Paid</th>
                    <th className="py-2 font-medium text-right">Outstanding</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.invoice_id} className="border-b hover:bg-accent/50">
                      <td className="py-2 font-mono text-xs">{i.invoice_no}</td>
                      <td className="py-2 max-w-[160px] truncate">{i.client}</td>
                      <td className="py-2"><Badge variant="outline" className="text-[10px]">{i.invoice_type.toUpperCase()}</Badge></td>
                      <td className="py-2 text-xs">{dateStr(i.date)}</td>
                      <td className="py-2 text-xs">{dateStr(i.due_date)}</td>
                      <td className="py-2 cell-num">{inr(i.amount, { compact: true })}</td>
                      <td className="py-2 cell-num text-muted-foreground">{inr(i.net_receivable, { compact: true })}</td>
                      <td className="py-2 cell-num text-emerald-700">{inr(i.paid_amount, { compact: true })}</td>
                      <td className="py-2 cell-num font-medium">{inr(i.outstanding, { compact: true })}</td>
                      <td className="py-2">
                        <Badge className={
                          i.status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                          i.is_overdue ? "bg-red-100 text-red-800 border-red-200" :
                          i.status === "partial" ? "bg-amber-100 text-amber-800 border-amber-200" :
                          "bg-slate-100 text-slate-700 border-slate-200"
                        }>
                          {i.is_overdue ? `Overdue ${i.days_overdue}d` : i.status}
                        </Badge>
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          <InvoicePdfButton invoiceId={i.invoice_id} invoiceNo={i.invoice_no} />
                          {i.outstanding > 0 && (
                            <Button size="sm" variant="outline" data-testid={`record-payment-${i.invoice_id}`} className="h-7 text-xs" onClick={() => setShowPayment(i)}>
                              Record payment
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="wbs" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-2 flex-row items-center justify-between gap-2">
              <div>
                <CardTitle className="text-sm font-semibold font-display">MIS by milestone</CardTitle>
                <div className="text-[11px] text-muted-foreground mt-0.5">Planned, spent, billed, collected and margin — sliced by every WBS phase</div>
              </div>
              <Select value={wbsProject} onValueChange={setWbsProject}>
                <SelectTrigger data-testid="mis-wbs-project" className="h-9 w-64"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>
                      <span className="font-mono text-xs mr-2 text-muted-foreground">{p.code}</span>{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="pt-0">
              {!wbsData ? (
                <div className="text-sm text-muted-foreground text-center py-8">Loading…</div>
              ) : (wbsData.rows || []).length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-8">No milestones defined for this project — create them in Finance → Milestones · WBS.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
                    <MiniStat label="Planned" value={wbsData.totals.planned_cost} data-testid="mis-wbs-planned" />
                    <MiniStat label="Committed" value={wbsData.totals.budget_committed} data-testid="mis-wbs-committed" />
                    <MiniStat label="Spent" value={wbsData.totals.spent} tone="warn" data-testid="mis-wbs-spent" />
                    <MiniStat label="Billed" value={wbsData.totals.billed} data-testid="mis-wbs-billed" />
                    <MiniStat label="Collected" value={wbsData.totals.collected} tone="good" data-testid="mis-wbs-collected" />
                    <MiniStat label="Vendor paid" value={wbsData.totals.vendor_paid} data-testid="mis-wbs-vendor-paid" />
                  </div>
                  {wbsData.untagged?.count > 0 && (
                    <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-3 w-3" />
                      {wbsData.untagged.count} untagged items · Spent {inr(wbsData.untagged.spent, { compact: true })} · Billed {inr(wbsData.untagged.billed, { compact: true })}
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="mis-wbs-table">
                      <thead>
                        <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                          <th className="py-2 font-medium">Code</th>
                          <th className="py-2 font-medium">Milestone</th>
                          <th className="py-2 font-medium">Status</th>
                          <th className="py-2 font-medium">P%/A%</th>
                          <th className="py-2 font-medium text-right">Planned</th>
                          <th className="py-2 font-medium text-right">Spent</th>
                          <th className="py-2 font-medium text-right">Billed</th>
                          <th className="py-2 font-medium text-right">Collected</th>
                          <th className="py-2 font-medium text-right">Vendor paid</th>
                          <th className="py-2 font-medium text-right">Margin</th>
                          <th className="py-2 font-medium text-center">CPI·SPI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wbsData.rows.map((r) => (
                          <tr key={r.wbs_id} className="border-b hover:bg-accent/50" data-testid={`mis-wbs-row-${r.wbs_id}`}>
                            <td className="py-2 font-mono text-xs">{r.code}</td>
                            <td className="py-2 max-w-[200px] truncate">{r.name}</td>
                            <td className="py-2"><Badge variant="outline" className="text-[10px]">{r.status}</Badge></td>
                            <td className="py-2 font-mono text-[10px]">{r.planned_pct}%/{r.actual_pct}%</td>
                            <td className="py-2 cell-num">{inr(r.planned_cost, { compact: true })}</td>
                            <td className="py-2 cell-num text-amber-800">{inr(r.spent, { compact: true })}</td>
                            <td className="py-2 cell-num">{inr(r.billed, { compact: true })}</td>
                            <td className="py-2 cell-num text-emerald-700">{inr(r.collected, { compact: true })}</td>
                            <td className="py-2 cell-num">{inr(r.vendor_paid, { compact: true })}</td>
                            <td className={`py-2 cell-num font-medium ${r.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>{inr(r.margin, { compact: true })}</td>
                            <td className="py-2 text-center text-[11px] font-mono">
                              <span className={r.cpi >= 1 ? "text-emerald-700" : r.cpi >= 0.9 ? "text-amber-700" : "text-red-700"}>{(r.cpi || 0).toFixed(2)}</span>
                              {" · "}
                              <span className={r.spi >= 1 ? "text-emerald-700" : r.spi >= 0.9 ? "text-amber-700" : "text-red-700"}>{(r.spi || 0).toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aging" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-display">Receivables aging</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={agingData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => inr(v, { compact: true, symbol: false })} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => inr(v, { compact: true })} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Bar dataKey="value" fill="#1A3636" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="client" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold font-display">Client-wise summary</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Client</th>
                    <th className="py-2 font-medium text-right">Total Billed</th>
                    <th className="py-2 font-medium text-right">Collected</th>
                    <th className="py-2 font-medium text-right">Outstanding</th>
                    <th className="py-2 font-medium text-right">Collection %</th>
                  </tr>
                </thead>
                <tbody>
                  {clientRows.map((c) => (
                    <tr key={c.client} className="border-b hover:bg-accent/50">
                      <td className="py-2 font-medium">{c.client}</td>
                      <td className="py-2 cell-num">{inr(c.billed, { compact: true })}</td>
                      <td className="py-2 cell-num text-emerald-700">{inr(c.collected, { compact: true })}</td>
                      <td className="py-2 cell-num text-amber-700">{inr(c.outstanding, { compact: true })}</td>
                      <td className="py-2 cell-num">{c.billed ? ((c.collected / c.billed) * 100).toFixed(1) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <RecordPaymentDialog invoice={showPayment} onClose={() => setShowPayment(null)} onSaved={load} />
    </div>
  );
}

function SendRemindersButton({ onDone }) {
  const [loading, setLoading] = useState(false);
  const send = async () => {
    setLoading(true);
    try {
      const r = await api.post("/reminders/send-overdue");
      toast.success(`${r.data.sent} reminder(s) sent · ${r.data.skipped} skipped`);
      onDone?.();
    } catch { toast.error("Failed to send reminders"); }
    finally { setLoading(false); }
  };
  return (
    <Button data-testid="send-reminders-btn" onClick={send} disabled={loading}
      variant="outline" className="h-9 border-secondary/50 hover:bg-secondary/10">
      <Send className="h-4 w-4 mr-1.5 text-secondary" />
      {loading ? "Sending…" : "Send overdue reminders"}
    </Button>
  );
}

function NewInvoiceDialog({ projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [wbsList, setWbsList] = useState([]);
  const [form, setForm] = useState({
    project_id: "", client: "", client_email: "", invoice_no: "", invoice_type: "ra",
    date: new Date().toISOString().slice(0, 10),
    due_date: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    amount: 0, gst_pct: 18, tds_pct: 2, retention_pct: 5, status: "raised", paid_amount: 0,
    wbs_id: "",
  });
  useEffect(() => {
    if (!form.project_id) { setWbsList([]); return; }
    api.get(`/wbs?project_id=${form.project_id}`).then((r) => setWbsList(r.data)).catch(() => setWbsList([]));
  }, [form.project_id]);
  const save = async () => {
    if (!form.project_id) return toast.error("Select project");
    const proj = projects.find((p) => p.project_id === form.project_id);
    try {
      await api.post("/invoices", { ...form, client: form.client || proj?.client || "", amount: Number(form.amount) });
      toast.success("Invoice raised"); setOpen(false); onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to raise invoice"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-invoice-btn" className="h-9 bg-primary hover:bg-primary/90"><Plus className="h-4 w-4 mr-1" />New invoice</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New invoice / RA bill</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Project" full>
            <Select value={form.project_id} onValueChange={(v) => {
              const p = projects.find((x) => x.project_id === v);
              setForm({...form, project_id: v, client: p?.client || ""});
            }}>
              <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Invoice #"><Input value={form.invoice_no} onChange={(e) => setForm({...form, invoice_no: e.target.value})} /></Field>
          <Field label="Client"><Input value={form.client} onChange={(e) => setForm({...form, client: e.target.value})} /></Field>
          <Field label="Client email (for reminders)" full><Input type="email" data-testid="invoice-client-email" placeholder="ap@clientcompany.in" value={form.client_email} onChange={(e) => setForm({...form, client_email: e.target.value})} /></Field>
          <Field label="Type">
            <Select value={form.invoice_type} onValueChange={(v) => setForm({...form, invoice_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ra">RA (Running Account)</SelectItem>
                <SelectItem value="final">Final</SelectItem>
                <SelectItem value="retention">Retention</SelectItem>
                <SelectItem value="advance">Advance</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Amount (₹)"><Input type="number" data-testid="invoice-amount" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></Field>
          <Field label="Milestone / WBS">
            <Select value={form.wbs_id || "__none"} onValueChange={(v) => setForm({ ...form, wbs_id: v === "__none" ? "" : v })}>
              <SelectTrigger data-testid="invoice-wbs"><SelectValue placeholder="— untagged —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— untagged —</SelectItem>
                {wbsList.map((w) => <SelectItem key={w.wbs_id} value={w.wbs_id}>{w.code} · {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} /></Field>
          <Field label="GST %"><Input type="number" value={form.gst_pct} onChange={(e) => setForm({...form, gst_pct: e.target.value})} /></Field>
          <Field label="TDS %"><Input type="number" value={form.tds_pct} onChange={(e) => setForm({...form, tds_pct: e.target.value})} /></Field>
          <Field label="Retention %"><Input type="number" value={form.retention_pct} onChange={(e) => setForm({...form, retention_pct: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button data-testid="invoice-save" onClick={save} className="bg-primary hover:bg-primary/90">Raise invoice</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentDialog({ invoice, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: 0, date: new Date().toISOString().slice(0, 10), mode: "bank", reference: "" });
  useEffect(() => {
    if (invoice) setForm({ amount: invoice.outstanding, date: new Date().toISOString().slice(0, 10), mode: "bank", reference: "" });
  }, [invoice]);
  if (!invoice) return null;
  const save = async () => {
    try {
      await api.post("/payments", { invoice_id: invoice.invoice_id, ...form, amount: Number(form.amount) });
      toast.success("Payment recorded");
      onClose(); onSaved();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Record payment · {invoice.invoice_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (₹)"><Input type="number" data-testid="payment-amount" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Mode">
            <Select value={form.mode} onValueChange={(v) => setForm({...form, mode: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["bank","rtgs","cheque","cash","upi"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Reference / UTR"><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button data-testid="payment-save" onClick={save} className="bg-primary hover:bg-primary/90">Save payment</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
